import { create } from 'zustand';
import {
  cloneGraphAIAssistConfig,
  createDefaultGraphAIAssistConfig,
  createEmptyPreviewTextSample,
  type CreateNodeDraft,
  type GraphAIAssistConfig,
  type PreviewTextSample,
} from '../../../core/entities/ai-assist-config';
import { applyDeidentificationRules } from '../../../core/policies/anonymization/apply-deidentification-rules';
import { assembleDeidentificationRules, createManualRule, reconcileManualRules } from '../../../core/policies/anonymization/assemble-deidentification-rules';
import { restoreDeidentificationRules } from '../../../core/policies/anonymization/restore-deidentification-rules';
import { applyCandidateScanToSettings, fingerprintGraphCandidateSources } from '../../../core/policies/anonymization/scan-deidentification-candidates';
import type { GraphDocument } from '../../../core/entities/graph-document';
import { useGraphStore } from '../../graph-view/services/graph-store';

interface DeidentificationSettingsState {
  currentGraphId: string | null;
  graphFingerprint: string;
  config: GraphAIAssistConfig;
  previewSample: PreviewTextSample;
  warnings: string[];
  humanOnly: boolean;
  initialize(graph: GraphDocument, config?: GraphAIAssistConfig, warnings?: string[]): void;
  syncWithGraph(graph: GraphDocument): void;
  setEnabled(enabled: boolean): void;
  setHumanOnly(humanOnly: boolean): void;
  setCandidateEnabled(candidateId: string, enabled: boolean): void;
  setCandidateReplacement(candidateId: string, replacementValue: string): void;
  addManualRule(originalValue: string, replacementValue: string): boolean;
  updateManualRule(ruleId: string, patch: { originalValue?: string; replacementValue?: string; enabled?: boolean }): boolean;
  removeManualRule(ruleId: string): void;
  setPromptTemplate(systemPromptTemplate: string): void;
  setCreateNodePromptTemplate(systemPromptTemplate: string): void;
  setCreateNodeExamplePayload(examplePayload: CreateNodeDraft): void;
  setPreviewInput(inputText: string): void;
  runPreview(): void;
  resetWarnings(warnings?: string[]): void;
}

export const useDeidentificationSettingsStore = create<DeidentificationSettingsState>((set, get) => ({
  currentGraphId: null,
  graphFingerprint: '',
  config: createDefaultGraphAIAssistConfig(),
  previewSample: createEmptyPreviewTextSample(),
  warnings: [],
  humanOnly: false,
  initialize(graph, config, warnings = []) {
    const baseConfig = cloneGraphAIAssistConfig(config ?? createDefaultGraphAIAssistConfig());
    const scannedSettings = applyCandidateScanToSettings(graph, baseConfig.deIdentification);
    set({
      currentGraphId: graph.graphId,
      graphFingerprint: fingerprintGraphCandidateSources(graph),
      config: {
        ...baseConfig,
        deIdentification: reconcileManualRules(scannedSettings),
      },
      previewSample: createEmptyPreviewTextSample(),
      warnings,
    });
  },
  syncWithGraph(graph) {
    const state = get();
    const fingerprint = fingerprintGraphCandidateSources(graph);
    if (state.currentGraphId !== graph.graphId) {
      state.initialize(graph);
      return;
    }
    if (fingerprint === state.graphFingerprint) {
      return;
    }
    set({
      graphFingerprint: fingerprint,
      config: {
        ...state.config,
        deIdentification: applyCandidateScanToSettings(graph, state.config.deIdentification),
      },
      warnings: state.warnings,
    });
  },
  setEnabled(enabled) {
    if (useGraphStore.getState().readonlyMode) return;
    set((state) => ({
      config: {
        ...state.config,
        deIdentification: {
          ...state.config.deIdentification,
          enabled,
          updatedAt: new Date().toISOString(),
        },
      },
    }));
  },
  setHumanOnly(humanOnly) {
    set({ humanOnly });
  },
  setCandidateEnabled(candidateId, enabled) {
    if (useGraphStore.getState().readonlyMode) return;
    set((state) => ({
      config: {
        ...state.config,
        deIdentification: {
          ...state.config.deIdentification,
          candidates: state.config.deIdentification.candidates.map((candidate) => candidate.candidateId === candidateId ? { ...candidate, enabled } : candidate),
          updatedAt: new Date().toISOString(),
        },
      },
    }));
  },
  setCandidateReplacement(candidateId, replacementValue) {
    if (useGraphStore.getState().readonlyMode) return;
    set((state) => ({
      config: {
        ...state.config,
        deIdentification: {
          ...state.config.deIdentification,
          candidates: state.config.deIdentification.candidates.map((candidate) => candidate.candidateId === candidateId ? {
            ...candidate,
            replacementValue: replacementValue.trim() || null,
            enabled: replacementValue.trim().length > 0 || candidate.candidateType === 'human-name',
            isCustomized: replacementValue.trim() !== (candidate.defaultReplacementValue ?? ''),
          } : candidate),
          updatedAt: new Date().toISOString(),
        },
      },
    }));
  },
  addManualRule(originalValue, replacementValue) {
    if (useGraphStore.getState().readonlyMode) return false;
    const trimmedOriginal = originalValue.trim();
    const trimmedReplacement = replacementValue.trim();
    if (!trimmedOriginal || !trimmedReplacement) {
      return false;
    }
    set((state) => ({
      config: {
        ...state.config,
        deIdentification: reconcileManualRules({
          ...state.config.deIdentification,
          manualRules: [...state.config.deIdentification.manualRules, createManualRule({ originalValue: trimmedOriginal, replacementValue: trimmedReplacement })],
          updatedAt: new Date().toISOString(),
        }),
      },
    }));
    return true;
  },
  updateManualRule(ruleId, patch) {
    if (useGraphStore.getState().readonlyMode) return false;
    const nextOriginal = patch.originalValue?.trim();
    const nextReplacement = patch.replacementValue?.trim();
    if ((patch.originalValue !== undefined && !nextOriginal) || (patch.replacementValue !== undefined && !nextReplacement)) {
      return false;
    }
    set((state) => ({
      config: {
        ...state.config,
        deIdentification: reconcileManualRules({
          ...state.config.deIdentification,
          manualRules: state.config.deIdentification.manualRules.map((rule) => rule.ruleId === ruleId ? {
            ...rule,
            originalValue: nextOriginal ?? rule.originalValue,
            replacementValue: nextReplacement ?? rule.replacementValue,
            enabled: patch.enabled ?? rule.enabled,
            updatedAt: new Date().toISOString(),
          } : rule),
          updatedAt: new Date().toISOString(),
        }),
      },
    }));
    return true;
  },
  removeManualRule(ruleId) {
    if (useGraphStore.getState().readonlyMode) return;
    set((state) => ({
      config: {
        ...state.config,
        deIdentification: {
          ...state.config.deIdentification,
          manualRules: state.config.deIdentification.manualRules.filter((rule) => rule.ruleId !== ruleId),
          updatedAt: new Date().toISOString(),
        },
      },
    }));
  },
  setPromptTemplate(systemPromptTemplate) {
    if (useGraphStore.getState().readonlyMode) return;
    set((state) => ({
      config: {
        ...state.config,
        promptContract: {
          ...state.config.promptContract,
          systemPromptTemplate,
        },
      },
    }));
  },
  setCreateNodePromptTemplate(systemPromptTemplate) {
    if (useGraphStore.getState().readonlyMode) return;
    set((state) => ({
      config: {
        ...state.config,
        createNodePromptContract: {
          ...state.config.createNodePromptContract,
          systemPromptTemplate,
        },
      },
    }));
  },
  setCreateNodeExamplePayload(examplePayload) {
    if (useGraphStore.getState().readonlyMode) return;
    set((state) => ({
      config: {
        ...state.config,
        createNodePromptContract: {
          ...state.config.createNodePromptContract,
          examplePayload,
        },
      },
    }));
  },
  setPreviewInput(inputText) {
    set((state) => ({
      previewSample: {
        ...state.previewSample,
        inputText,
      },
    }));
  },
  runPreview() {
    const state = get();
    const assembly = assembleDeidentificationRules(state.config.deIdentification);
    const masked = applyDeidentificationRules(state.previewSample.inputText, assembly.activeRules);
    const restored = restoreDeidentificationRules(masked.text, assembly.activeRules);
    set({
      previewSample: {
        inputText: state.previewSample.inputText,
        maskedText: masked.text,
        restoredText: restored.text,
        issues: [...assembly.blockedRules.map((rule) => `Rule "${rule.originalValue}" is blocked by a restore conflict.`), ...restored.issues],
      },
    });
  },
  resetWarnings(warnings = []) {
    set({ warnings });
  },
}));

export function initializeDeidentificationSettings(graph: GraphDocument, config?: GraphAIAssistConfig, warnings?: string[]) {
  useDeidentificationSettingsStore.getState().initialize(graph, config, warnings);
}

export function hydrateDeidentificationSettings(graph: GraphDocument, config?: GraphAIAssistConfig, warnings?: string[]) {
  initializeDeidentificationSettings(graph, config, warnings);
}

export function getCurrentGraphAIAssistConfig(): GraphAIAssistConfig {
  return useDeidentificationSettingsStore.getState().config;
}

let deidentificationGraphSyncStarted = false;

export function ensureDeidentificationGraphSync() {
  if (deidentificationGraphSyncStarted) {
    return;
  }
  deidentificationGraphSyncStarted = true;
  initializeDeidentificationSettings(useGraphStore.getState().graph);
  useGraphStore.subscribe((state, previousState) => {
    if (state.graph !== previousState.graph) {
      useDeidentificationSettingsStore.getState().syncWithGraph(state.graph);
    }
  });
}
